import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLiveTaskNames } from "@/hooks/usePeople";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { toast } from "sonner";
import { useTableSubscription } from "@/hooks/useTableSubscription";
import { taskListChannelName } from "@/lib/taskRealtimeChannels";

export interface TaskAssignment {
  id: string;
  title: string;
  description: string | null;
  assigned_to: string;
  assigned_to_name: string | null;
  assigned_by: string;
  assigned_by_name: string | null;
  status: "open" | "in_progress" | "done";
  priority: "low" | "normal" | "high" | "urgent";
  due_date: string | null;
  notes: string | null;
  review_notes: string | null;
  completed_at: string | null;
  reviewed_at: string | null;
  reviewed_by: string | null;
  created_at: string;
  updated_at: string;
}

export function useMyTasks() {
  const { user } = useAuth();

  const liveNames = useLiveTaskNames<TaskAssignment>();
  return useQuery({
    queryKey: ["my-task-assignments", user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from("task_assignments")
        .select("*")
        .eq("assigned_to", user.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []) as TaskAssignment[];
    },
    enabled: !!user,
    select: liveNames,
  });
}

export function useAllTasks() {
  const liveNames = useLiveTaskNames<TaskAssignment>();
  return useQuery({
    queryKey: ["all-task-assignments"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("task_assignments")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []) as TaskAssignment[];
    },
    select: liveNames,
  });
}

export function useCreateTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (task: {
      title: string;
      description?: string;
      assigned_to: string;
      assigned_to_name?: string;
      assigned_by: string;
      assigned_by_name?: string;
      priority?: string;
      due_date?: string;
      notes?: string;
    }) => {
      const { data, error } = await supabase
        .from("task_assignments")
        .insert(task)
        .select()
        .single();
      if (error) throw error;

      // The notification is created server-side by the task_assignments triggers (migration 20400000004000).

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["all-task-assignments"] });
      queryClient.invalidateQueries({ queryKey: ["my-task-assignments"] });
      queryClient.invalidateQueries({ queryKey: ["my-unified-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      toast.success("The task has been assigned successfully.");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

/**
 * useTaskAssignmentsRealtime — subscribes to live updates for task_assignments.
 *
 * Mount this once at the Tasks page level. Any INSERT, UPDATE, or DELETE on
 * task_assignments invalidates all task query keys so components refetch
 * immediately rather than waiting for manual page refresh.
 *
 * Prerequisites (Supabase dashboard): Realtime must be enabled for
 * task_assignments in Table Editor → Realtime toggle.
 */
export function useTaskAssignmentsRealtime(): void {
  const { user } = useAuth();
  // Must NOT share a name with TaskNotificationListener's channel: realtime-js
  // hands back the existing channel for a duplicate name, and this hook's
  // unmount would then remove the app-wide listener (taskRealtimeChannels.ts).
  useTableSubscription(taskListChannelName(user?.id ?? "anon"), [
    {
      table: "task_assignments",
      invalidateKeys: [
        ["all-task-assignments"],
        ["my-task-assignments"],
        ["my-unified-tasks"],
        ["task-alerts-inbox"],
        ["task-alerts-sent"],
        ["task-alerts-completed"],
        ["task-alerts-stats"],
        ["notifications"],
      ],
    },
  ]);
}

export function useUpdateTaskStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const update: Record<string, string> = { status };
      if (status === "done") update.completed_at = new Date().toISOString();
      const { error } = await supabase.from("task_assignments").update(update).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["all-task-assignments"] });
      queryClient.invalidateQueries({ queryKey: ["my-task-assignments"] });
      queryClient.invalidateQueries({ queryKey: ["my-unified-tasks"] });
      toast.success("Task status has been updated.");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}
